import {
  AGENCY_ID,
  AGENCY_NAME,
  AUTH_ENABLED,
  CITIZEN_PORTAL_SESSION_LAST_ACTIVITY_KEY,
  CITIZEN_PORTAL_SESSION_TIMEOUT_MS,
  CRM_ISSUE_ID,
  CRM_TOKEN,
  PENDING_FOLLOW_ISSUE_ID,
  PRIVATE_APP_TOKEN,
  PrivateSubmissionAppRoutes,
} from '@private-crm-app/constants';

import { getFormattedDeviceDetails } from '@utils/DeviceDetailsUtil';
import { MIXPANEL_AGENCY_NAME_PROP, track } from '@utils/MixPanelUtils';

/** First portal surface hit in a citizen session. */
export type CitizenPortalLandingPage =
  | 'all_requests'
  | 'my_requests'
  | 'submission'
  | 'request_details'
  | 'profile'
  | 'onboarding'
  | 'other';

const CITIZEN_PORTAL_NON_TRACKABLE_ROUTES = [
  PrivateSubmissionAppRoutes.ERROR,
  PrivateSubmissionAppRoutes.NAVIGATION_ERROR,
  PrivateSubmissionAppRoutes.MISSING_TOKEN_ERROR,
  PrivateSubmissionAppRoutes.LOGOUT_HANDLER,
] as const;

export const getCRMToken = () => localStorage.getItem(CRM_TOKEN);

export const getCRMTokenFromUrlOrStorage = (search?: string): string | null => {
  const urlParams = new URLSearchParams(search ?? window.location.search);
  const urlToken = urlParams.get('crm_token');
  if (urlToken) return urlToken;
  return getCRMToken();
};

export const getPrivateAppToken = () => localStorage.getItem(PRIVATE_APP_TOKEN);

export const getCitizenAuthHeaders = (): Record<string, string> => {
  const token = getPrivateAppToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getAgencyId = () => Number(localStorage.getItem(AGENCY_ID));

export const getPublicMapUrl = (crmToken: string | null | undefined): string => {
  if (!crmToken) {
    return PrivateSubmissionAppRoutes.PUBLIC_MAP;
  }
  return `${PrivateSubmissionAppRoutes.PUBLIC_MAP}?crm_token=${encodeURIComponent(crmToken)}`;
};

export const buildIssueUrl = (crmIssueId: string | number, crmToken?: string | null): string => {
  const base = `${PrivateSubmissionAppRoutes.ISSUE_BASE_PATH}/${crmIssueId}`;
  return crmToken ? `${base}?crm_token=${encodeURIComponent(crmToken)}` : base;
};

/**
 * Safely parse JSON from localStorage or other string sources.
 * Returns null if the string is null/empty or if parsing fails (malformed JSON).
 */
export const safeParseJson = <T = unknown>(str: string | null): T | null => {
  if (!str) return null;
  try {
    return JSON.parse(str) as T;
  } catch {
    return null;
  }
};

/** Minimal shape used when reading submission from localStorage */
export interface StoredSubmissionShape {
  id?: number;
  magicLink?: string;
}

/** Safely read and parse the submission object from localStorage. */
export const getStoredSubmission = (): StoredSubmissionShape | null =>
  safeParseJson<StoredSubmissionShape>(localStorage.getItem('submission'));

export const clearPendingFollowIntent = (options?: { clearCrmIssueId?: boolean }): void => {
  localStorage.removeItem(PENDING_FOLLOW_ISSUE_ID);
  if (options?.clearCrmIssueId) {
    localStorage.removeItem(CRM_ISSUE_ID);
  }
};

export const getAgencyName = () => localStorage.getItem(AGENCY_NAME);

/** Persist CRM token session; stores Agency.name in agency_name (never CRM product name). */
export const persistCrmTokenSession = (
  crmToken: string,
  response: Pick<IAgencyDetails, 'agencyId' | 'agencyName'>
): void => {
  localStorage.setItem(CRM_TOKEN, crmToken);
  localStorage.setItem(AGENCY_ID, response.agencyId.toString());

  if (response.agencyName) {
    localStorage.setItem(AGENCY_NAME, response.agencyName);
  } else {
    localStorage.removeItem(AGENCY_NAME);
  }
};

export const clearCrmTokenSession = (): void => {
  localStorage.removeItem(CRM_TOKEN);
  localStorage.removeItem(AGENCY_ID);
  localStorage.removeItem(AGENCY_NAME);
};

export const isAuthEnabled = () => localStorage.getItem(AUTH_ENABLED) === 'true';

export function generateLightColor() {
  const r = Math.floor(Math.random() * 127) + 128; // Range: 128-255
  const g = Math.floor(Math.random() * 127) + 128; // Range: 128-255
  const b = Math.floor(Math.random() * 127) + 128; // Range: 128-255

  return `rgb(${r}, ${g}, ${b})`;
}

export const stripHtml = (html: string): string => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

export const formatDateToMMDDYYYY = (dateStr: string): string => {
  const date = new Date(dateStr);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const getCommonProperties = (isAuthenticated: boolean) => ({
  'Device Details': getFormattedDeviceDetails(),
  [MIXPANEL_AGENCY_NAME_PROP]: getAgencyName(),
  'Logged In': isAuthenticated,
});

/** Shared props for Citizen - Map:* events (canonical Agency Name. */
const getCitizenMapMixpanelProps = (isAuthenticated: boolean) => ({
  logged_in: isAuthenticated,
  [MIXPANEL_AGENCY_NAME_PROP]: getAgencyName(),
});

export const trackRequestStarted = (name: string, email: string, isAuthenticated: boolean) => {
  track(isAuthenticated ? 'Logged in request started' : 'Request started', {
    ...getCommonProperties(isAuthenticated),
    ...(isAuthenticated
      ? {
          $name: name,
          $email: email,
        }
      : {}),
  });
};

export const trackRequestSubmitted = (category: string, name: string, email: string, isAuthenticated: boolean) => {
  track('Request submitted', {
    ...getCommonProperties(isAuthenticated),
    'Request Category': category,
    ...(isAuthenticated
      ? {
          $name: name,
          $email: email,
        }
      : {}),
  });
};

export const trackSubmitAnonymous = (category: string) => {
  track('Submit anonymously', {
    ...getCommonProperties(false),
    'Request Category': category,
  });
};

export const trackRequestCreated = (category: string, name?: string, email?: string, isAuthenticated?: boolean) => {
  track('Request created', {
    ...getCommonProperties(isAuthenticated),
    'Request Category': category,
    ...(isAuthenticated
      ? {
          $name: name,
          $email: email,
        }
      : {}),
  });
};

export const trackViewProfile = (name: string, email: string) => {
  track('View profile', {
    ...getCommonProperties(true),
    $name: name,
    $email: email,
  });
};

export const trackUpdateProfile = (name: string, email: string) => {
  track('Update profile', {
    ...getCommonProperties(true),
    $name: name,
    $email: email,
  });
};

export const trackViewMyRequests = (name: string, email: string) => {
  track('View My Requests', {
    ...getCommonProperties(true),
    $name: name,
    $email: email,
  });
};

export const trackViewAllRequests = (name: string, email: string) => {
  track('View All Requests', {
    ...getCommonProperties(true),
    $name: name,
    $email: email,
  });
};

export const trackViewOpenRequests = (name: string, email: string) => {
  track('View Open Requests', {
    ...getCommonProperties(true),
    $name: name,
    $email: email,
  });
};

export const trackViewClosedRequests = (name: string, email: string) => {
  track('View Closed Requests', {
    ...getCommonProperties(true),
    $name: name,
    $email: email,
  });
};

export const trackViewRequestDetails = (name: string, email: string) => {
  track('View Request Details', {
    ...getCommonProperties(true),
    $name: name,
    $email: email,
  });
};

export const trackViewAttachment = (fileName: string) => {
  track('View Attachment', {
    ...getCommonProperties(true),
    'File Name': fileName,
  });
};

export const trackAddAttachment = (name: string, email: string, attachmentName: string) => {
  track('Add Attachment', {
    ...getCommonProperties(true),
    $name: name,
    $email: email,
    'Attachment Name': attachmentName,
  });
};

export const trackSendMessage = (name: string, email: string) => {
  track('Send Message', {
    ...getCommonProperties(true),
    $name: name,
    $email: email,
  });
};

export const trackPublicMapViewed = (isAuthenticated: boolean, submissionCount: number, defaultFilter: string) => {
  track('Citizen - Map: Viewed', {
    ...getCitizenMapMixpanelProps(isAuthenticated),
    submission_count: submissionCount,
    default_filter: defaultFilter,
  });
};

/** True for real portal surfaces; false for error/logout infrastructure routes. */
export const isCitizenPortalTrackablePath = (pathname: string): boolean =>
  !CITIZEN_PORTAL_NON_TRACKABLE_ROUTES.some((route) => pathname.includes(route));

/** Map a citizen portal pathname to Mixpanel landing_page (unknown → other). */
export const getCitizenPortalLandingPage = (pathname: string): CitizenPortalLandingPage => {
  if (pathname.includes(PrivateSubmissionAppRoutes.PUBLIC_MAP)) {
    return 'all_requests';
  }
  if (pathname.includes(PrivateSubmissionAppRoutes.LISTING)) {
    return 'my_requests';
  }
  if (pathname.includes(PrivateSubmissionAppRoutes.CREATE)) {
    return 'submission';
  }
  if (pathname.includes(PrivateSubmissionAppRoutes.ISSUE_BASE_PATH)) {
    return 'request_details';
  }
  if (pathname.includes(PrivateSubmissionAppRoutes.PROFILE)) {
    return 'profile';
  }
  if (pathname.includes(PrivateSubmissionAppRoutes.ONBOARDING)) {
    return 'onboarding';
  }

  return 'other';
};

export const getCitizenPortalSessionLastActivity = (): number | null => {
  const raw = localStorage.getItem(CITIZEN_PORTAL_SESSION_LAST_ACTIVITY_KEY);
  if (!raw) {
    return null;
  }
  const parsed = Number(raw);

  return Number.isFinite(parsed) ? parsed : null;
};

/** Whether an open portal session exists (activity within the 30-minute idle window). */
export const isCitizenPortalSessionActive = (now = Date.now()): boolean => {
  const lastActivity = getCitizenPortalSessionLastActivity();
  if (lastActivity == null) {
    return false;
  }

  // Reject future timestamps (clock skew / corrupt LS); negative elapsed must not count as active.
  return lastActivity <= now && now - lastActivity < CITIZEN_PORTAL_SESSION_TIMEOUT_MS;
};

export const touchCitizenPortalSessionActivity = (now = Date.now()): void => {
  localStorage.setItem(CITIZEN_PORTAL_SESSION_LAST_ACTIVITY_KEY, String(now));
};

/** Session-start denominator for citizen adoption metrics. */
export const trackCitizenPortalVisited = (
  isAuthenticated: boolean,
  landingPage: CitizenPortalLandingPage
): void => {
  track('Citizen - Portal Visited', {
    logged_in: isAuthenticated,
    [MIXPANEL_AGENCY_NAME_PROP]: getAgencyName(),
    landing_page: landingPage,
  });
};

/**
 * Fire Citizen - Portal Visited once per 30-min idle session on trackable surfaces.
 * Always refreshes the rolling last-activity timestamp when the path is trackable.
 * @returns true when the Mixpanel event was sent
 */
export const ensureCitizenPortalVisited = (
  isAuthenticated: boolean,
  pathname: string,
  now = Date.now()
): boolean => {
  if (!isCitizenPortalTrackablePath(pathname)) {
    return false;
  }

  if (isCitizenPortalSessionActive(now)) {
    touchCitizenPortalSessionActivity(now);
    return false;
  }

  // Mark activity before track so a duplicate call in the same session cannot double-fire.
  touchCitizenPortalSessionActivity(now);
  trackCitizenPortalVisited(isAuthenticated, getCitizenPortalLandingPage(pathname));

  return true;
};

export const trackPublicMapFilterApplied = (
  isAuthenticated: boolean,
  filterType: string,
  filterValue: string,
  resultCount: number
) => {
  track('Citizen - Map: Filter Applied', {
    ...getCitizenMapMixpanelProps(isAuthenticated),
    filter_type: filterType,
    filter_value: filterValue,
    result_count: resultCount,
  });
};

export const trackPublicMapSubmissionDetailViewed = (
  isAuthenticated: boolean,
  submissionCategory: string,
  submissionStatus: string,
  followerCount: number,
  source: string
) => {
  track('Citizen - Map: Submission Detail Viewed', {
    ...getCitizenMapMixpanelProps(isAuthenticated),
    submission_category: submissionCategory,
    submission_status: submissionStatus,
    follower_count: followerCount,
    source,
  });
};

export const trackPublicMapSubmitRequestClicked = (isAuthenticated: boolean) => {
  track('Citizen - Map: Submit Request Clicked', {
    ...getCitizenMapMixpanelProps(isAuthenticated),
  });
};

export const trackPublicMapFollowClicked = (
  isAuthenticated: boolean,
  submissionCategory: string,
  submissionStatus: string
) => {
  track('Citizen - Map: Follow Clicked', {
    ...getCitizenMapMixpanelProps(isAuthenticated),
    submission_category: submissionCategory,
    submission_status: submissionStatus,
  });
};

export const trackPublicMapFollowClickedDetail = (
  isAuthenticated: boolean,
  submissionCategory: string,
  submissionStatus: string
) => {
  track('Citizen - Map: Follow Clicked - Detail', {
    ...getCitizenMapMixpanelProps(isAuthenticated),
    submission_category: submissionCategory,
    submission_status: submissionStatus,
  });
};

export const trackPublicMapDuplicateSuggestionShown = (
  isAuthenticated: boolean,
  suggestionCount: number,
  submissionCategory: string
) => {
  track('Citizen - Map: Duplicate Suggestion Shown', {
    ...getCitizenMapMixpanelProps(isAuthenticated),
    suggestion_count: suggestionCount,
    submission_category: submissionCategory,
  });
};

export const trackPublicMapFollowClickedDuplicateSkip = (isAuthenticated: boolean, submissionCountShown: number) => {
  track('Citizen - Map: Follow Clicked - Duplicate Skip', {
    ...getCitizenMapMixpanelProps(isAuthenticated),
    submission_count_shown: submissionCountShown,
  });
};

export const redirectTo = (url: string): void => {
  window.location.href = url;
};

/** http(s) URL safe to put on a portal header link. Adds https:// when the stored value has no scheme. */
export const toAbsoluteNavigationUrl = (value: string | null | undefined): string | null => {
  if (!value?.trim()) {
    return null;
  }

  const trimmed = value.trim();
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const parsed = new URL(candidate);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }

    return candidate;
  } catch {
    return null;
  }
};

export const getElevioPortalHomeUrl = (): string | null => {
  const hostname = window.location.hostname;
  const isStaging = hostname.includes('app-stage');
  const isDevelopmentOrQA = hostname.includes('app-qa') || hostname.includes('localhost');

  if (isStaging) {
    return 'https://comcate-stage.elevio.help/en';
  }
  if (isDevelopmentOrQA) {
    return 'https://comcate-development.elevio.help/en';
  }

  // No safe production-wide fallback exists: each agency has its own portal URL,
  // which is stored in localStorage as logout_redirect_url. If it is missing
  // (e.g. localStorage cleared, or first visit without the query param), returning
  // null lets callers show the logged-out page in place rather than redirecting to
  // the wrong agency portal.
  return null;
}